import { useState, useRef, useEffect } from 'react';
import { CMS_MERGE_TAG_GROUPS, toPlaceholder } from '../../../lib/cmsMergeTags.ts';

interface VariablePickerProps {
  /** Called with the full placeholder string, e.g. "{{customer.firstName}}" */
  onInsert: (placeholder: string) => void;
  /** Optional className for the trigger button */
  className?: string;
}

/**
 * A popover that lists CMS merge tags grouped by category.
 * Click a tag → calls onInsert with "{{tag.key}}".
 */
export function VariablePicker({ onInsert, className }: VariablePickerProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div ref={ref} className="relative inline-flex">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        title="Insert dynamic variable"
        className={
          className ??
          'shrink-0 flex items-center justify-center w-8 h-8 rounded-md bg-zinc-900 hover:bg-zinc-800 text-white text-xs font-bold transition-colors'
        }
      >
        {'{x}'}
      </button>

      {open && (
        <div className="absolute z-[100] top-full right-0 mt-1 w-64 max-h-80 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-xl">
          {CMS_MERGE_TAG_GROUPS.map((group) => (
            <div key={group.id}>
              <div className="sticky top-0 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-500 bg-gray-50 border-b border-gray-200">
                {group.icon} {group.name}
              </div>
              {group.tags.map((tag) => (
                <button
                  key={tag.key}
                  type="button"
                  onClick={() => {
                    onInsert(toPlaceholder(tag.key));
                    setOpen(false);
                  }}
                  className="w-full flex items-center justify-between px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <span className="font-medium">{tag.label}</span>
                  <span className="text-[10px] text-gray-400 font-mono">{tag.example}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
